/**
 * Security Service
 * Handles security settings, AES key rotation, password validation, and session management
 */

import Parse from 'parse'

export interface SecuritySettings {
  password: {
    minLength: number
    requireUppercase: boolean
    requireLowercase: boolean
    requireNumbers: boolean
    requireSpecialChars: boolean
    maxAge: number
    preventReuse: number
  }
  session: {
    timeout: number
    maxConcurrent: number
    rememberMe: boolean
    secureOnly: boolean
  }
  rateLimiting: {
    enabled: boolean
    requestsPerMinute: number
    requestsPerHour: number
  }
  twoFactor: {
    enabled: boolean
    required: boolean
    methods: ('email' | 'sms' | 'totp')[]
  }
  ipRestrictions: {
    enabled: boolean
    whitelist: string[]
    blacklist: string[]
  }
  encryption: {
    algorithm: 'AES-256' | 'AES-128'
    keyRotation: number
    currentKeyId?: string
    lastRotation?: string
    nextRotation?: string
  }
}

export interface PasswordValidationResult {
  isValid: boolean
  errors: string[]
}

class SecurityService {
  private static instance: SecurityService

  private constructor() {}

  static getInstance(): SecurityService {
    if (!SecurityService.instance) {
      SecurityService.instance = new SecurityService()
    }
    return SecurityService.instance
  }

  /**
   * Public password policy for signup/login UI (no auth required).
   */
  async getPasswordPolicy(): Promise<SecuritySettings['password']> {
    try {
      const result = await Parse.Cloud.run('getPasswordPolicy')

      if (!result.success) {
        throw new Error('Failed to get password policy')
      }

      return result.password
    } catch (error) {
      console.error('Get password policy error:', error)
      throw error
    }
  }

  /**
   * Get current security settings
   */
  async getSecuritySettings(): Promise<SecuritySettings> {
    try {
      const result = await Parse.Cloud.run('getSecuritySettings')
      
      if (!result.success) {
        throw new Error('Failed to get security settings')
      }
      
      return result.settings
    } catch (error) {
      console.error('Get security settings error:', error)
      throw error
    }
  }

  /**
   * Update security settings
   */
  async updateSecuritySettings(settings: Partial<SecuritySettings>): Promise<void> {
    try {
      const result = await Parse.Cloud.run('updateSecuritySettings', { settings })
      
      if (!result.success) {
        throw new Error('Failed to update security settings')
      }
    } catch (error) {
      console.error('Update security settings error:', error)
      throw error
    }
  }

  /**
   * Rotate AES encryption key
   */
  async rotateAESKey(): Promise<{ keyId: string; nextRotation: string }> {
    try {
      const result = await Parse.Cloud.run('rotateAESKey')
      
      if (!result.success) {
        throw new Error('Failed to rotate AES key')
      }
      
      return {
        keyId: result.keyId,
        nextRotation: result.nextRotation
      }
    } catch (error) {
      console.error('Rotate AES key error:', error)
      throw error
    }
  }

  /**
   * Validate password against security settings
   */
  async validatePassword(password: string): Promise<PasswordValidationResult> {
    try {
      const result = await Parse.Cloud.run('validatePassword', { password })
      
      if (!result.success) {
        throw new Error('Failed to validate password')
      }
      
      return {
        isValid: result.isValid,
        errors: result.errors
      }
    } catch (error) {
      console.error('Validate password error:', error)
      throw error
    }
  }

  /**
   * Client-side password validation (for immediate feedback)
   */
  validatePasswordLocally(password: string, settings: SecuritySettings): PasswordValidationResult {
    const errors: string[] = []
    
    // Check minimum length
    if (password.length < settings.password.minLength) {
      errors.push(`Password must be at least ${settings.password.minLength} characters long`)
    }
    
    // Check for uppercase letters
    if (settings.password.requireUppercase && !/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter")
    }
    
    // Check for lowercase letters
    if (settings.password.requireLowercase && !/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter")
    }
    
    // Check for numbers
    if (settings.password.requireNumbers && !/\d/.test(password)) {
      errors.push("Password must contain at least one number")
    }
    
    // Check for special characters
    if (settings.password.requireSpecialChars && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push("Password must contain at least one special character")
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * Format key rotation date for display
   */
  formatKeyRotationDate(dateString: string): string {
    if (!dateString) return 'Not set'
    
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Get time until next key rotation
   */
  getTimeUntilNextRotation(nextRotation: string): string {
    if (!nextRotation) return 'Not scheduled'
    
    const nextDate = new Date(nextRotation)
    const now = new Date()
    const diff = nextDate.getTime() - now.getTime()
    
    if (diff <= 0) {
      return 'Due for rotation'
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`
    } else {
      return `${hours} hour${hours !== 1 ? 's' : ''}`
    }
  }

  /**
   * Check if key rotation is overdue
   */
  isKeyRotationOverdue(nextRotation: string): boolean {
    if (!nextRotation) return false
    
    const nextDate = new Date(nextRotation)
    const now = new Date()
    
    return now >= nextDate
  }
}

export default SecurityService 